import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import type { CopiaCifsLoteResponse } from '@triade/contracts';
import { useCopiarCifsLote, useGradeCopiaCifs } from '@/api/cifs';
import { usePode } from '@/api/me';
import { ApropriacaoPorCentro } from '@/components/apropriacao/apropriacao-por-centro';
import { rotuloCompetencia } from '@/components/centros-custo/tipos';
import { ReabrirRateioDialog } from '@/components/rateios/reabrir-rateio-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MonthPicker } from '@/components/ui/month-picker';
import { ApiError } from '@/lib/api';
import { formatarMoeda } from '@/lib/format';
import {
  colarColuna,
  corpoLote,
  entradaDeResposta,
  linhasIniciais,
  resumoApropriacao,
  totaisGrade,
  valorDigitado,
  type LinhaGrade,
} from './grade-copia';

const COMPETENCIA = /^\d{4}-(0[1-9]|1[0-2])$/;
const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';

/** Mes anterior de AAAA-MM (so aritmetica de competencia; nenhum valor). */
export function mesAnteriorDe(comp: string): string {
  const [ano, mes] = comp.split('-').map(Number);
  const d = new Date(ano ?? 1970, (mes ?? 1) - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * "Copiar do mes": grade com todas as CIFs da origem. Cada linha tem o NOVO VALOR (digitado, colado do Excel) e a apropriacao
 * (da origem ou editada). A soma e o total sao so para mostrar; quem decide o que e pulado, o destino fechado e o rateio e a API.
 * `onDestinoFechado` avisa a tela quando o rateio do destino esta fechado (a tela desabilita o botao de abrir).
 */
export function CopiarCifsDialog({
  destino,
  onFechar,
  onDestinoFechado,
}: {
  destino: string;
  onFechar: () => void;
  onDestinoFechado: () => void;
}) {
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rateio.editar');

  const [origem, setOrigem] = useState(() => mesAnteriorDe(destino));
  const [destinoAtual, setDestinoAtual] = useState(destino);
  const [linhas, setLinhas] = useState<LinhaGrade[]>([]);
  const [fechadoPara, setFechadoPara] = useState<string | null>(null);
  const [erroCopia, setErroCopia] = useState<string | null>(null);
  const [resultado, setResultado] = useState<CopiaCifsLoteResponse | null>(null);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  /** Sobe a cada "Usar a da origem": reinicia o editor de apropriacao. */
  const [versaoEditor, setVersaoEditor] = useState(0);
  const [reabrirAberto, setReabrirAberto] = useState(false);
  const campos = useRef(new Map<string, HTMLInputElement>());

  const origemValida = COMPETENCIA.test(origem);
  const destinoValido = COMPETENCIA.test(destinoAtual);
  const mesmoMes = origem === destinoAtual;
  const habilitado = origemValida && destinoValido && !mesmoMes;
  const grade = useGradeCopiaCifs(origem, destinoAtual, habilitado);
  const copiar = useCopiarCifsLote();
  const dados = habilitado ? grade.data : undefined;
  const destinoFechado = dados?.destinoFechado === true || fechadoPara === destinoAtual;

  useEffect(() => {
    setLinhas(dados ? linhasIniciais(dados) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  useEffect(() => {
    if (destinoFechado && destinoAtual === destino) onDestinoFechado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoFechado]);

  const totais = dados ? totaisGrade(linhas, dados.totalDestinoAtual) : null;
  const corpo = dados ? corpoLote(linhas, origem, destinoAtual) : null;
  const podeCopiar = corpo !== null && !destinoFechado && !copiar.isPending && !grade.isLoading;
  const editando = linhas.find((l) => l.cifId === editandoId);
  const habilitadas = linhas.filter((l) => !l.existeNoDestino);
  const todasMarcadas = habilitadas.length > 0 && habilitadas.every((l) => l.marcada);

  function alterar(cifId: string, mudanca: Partial<LinhaGrade>) {
    setLinhas((atual) => atual.map((l) => (l.cifId === cifId ? { ...l, ...mudanca } : l)));
  }

  function marcarTodas(marcada: boolean) {
    setLinhas((atual) => atual.map((l) => (l.existeNoDestino ? l : { ...l, marcada })));
  }

  /** Enter: foca a linha habilitada seguinte (Shift+Enter, a anterior). Linhas ja existentes no destino sao puladas. */
  function focarVizinha(indice: number, direcao: 1 | -1) {
    for (let j = indice + direcao; j >= 0 && j < linhas.length; j += direcao) {
      const alvo = linhas[j]!;
      if (!alvo.existeNoDestino) {
        campos.current.get(alvo.cifId)?.focus();
        return;
      }
    }
  }

  function teclar(e: KeyboardEvent<HTMLInputElement>, indice: number) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    focarVizinha(indice, e.shiftKey ? -1 : 1);
  }

  /** Colar do Excel a partir da linha atual; a linha que recebeu valor passa a ser marcada. */
  function colar(e: ClipboardEvent<HTMLInputElement>, indice: number) {
    e.preventDefault();
    const texto = e.clipboardData.getData('text');
    setLinhas((atual) =>
      colarColuna(atual, indice, texto).map((l, i) => (l.texto !== atual[i]?.texto && !l.existeNoDestino ? { ...l, marcada: true } : l)),
    );
  }

  function copiarAgora() {
    if (!corpo) return;
    setErroCopia(null);
    copiar.mutate(corpo, {
      onSuccess: (r) => {
        // O front so envia as linhas marcadas e habilitadas; as que ja existiam no destino (desabilitadas na grade) tambem contam como puladas.
        const jaExistiam = linhas.filter((l) => l.existeNoDestino).map((l) => ({ identificador: l.identificador, descricao: l.descricao }));
        const vistos = new Set(r.pulados.map((p) => p.identificador));
        const pulados = [...r.pulados, ...jaExistiam.filter((p) => !vistos.has(p.identificador))];
        toast.success(`${r.copiadas} copiadas, ${pulados.length} puladas`);
        setResultado({ ...r, puladas: pulados.length, pulados });
      },
      onError: (e) => {
        if (e instanceof ApiError && e.code === 'RATEIO_FECHADO') setFechadoPara(destinoAtual);
        setErroCopia(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'Falha de comunicação com a API');
      },
    });
  }

  function irParaRecalculo() {
    void navigate({ to: '/custos/rateios', search: { comp: destinoAtual } });
  }

  if (resultado) {
    return (
      <Dialog open onOpenChange={(proximo) => { if (!proximo) onFechar(); }}>
        <DialogContent className="w-[min(1180px,96vw)] max-w-none sm:max-w-none">
          <DialogHeader>
            <DialogTitle>Copiar do mês…</DialogTitle>
            <DialogDescription>{`Lançamentos gravados em ${rotuloCompetencia(destinoAtual)}.`}</DialogDescription>
          </DialogHeader>
          <div role="status" className="flex flex-col gap-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <p className="font-semibold">{`${resultado.copiadas} copiadas, ${resultado.puladas} puladas`}</p>
            <p>Total copiado: <span className="tabular font-semibold">{formatarMoeda(resultado.totalCopiado)}</span></p>
            {resultado.semApropriacao > 0 && <p>{`${resultado.semApropriacao} sem apropriação — apropriar à mão`}</p>}
            {resultado.pulados.length > 0 && (
              <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-5 text-ink-secondary" aria-label="CIFs puladas">
                {resultado.pulados.map((p) => (
                  <li key={p.identificador}>
                    <span className="font-medium">{p.identificador}</span> — {p.descricao}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={irParaRecalculo}>{`Recalcular rateio de ${rotuloCompetencia(destinoAtual)}`}</Button>
            <Button onClick={onFechar}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  let aviso: string | null = null;
  if (!origemValida) aviso = 'Informe a competência de origem (AAAA-MM).';
  else if (!destinoValido) aviso = 'Informe a competência de destino (AAAA-MM).';
  else if (mesmoMes) aviso = 'A origem é a mesma competência do destino. Escolha outro mês.';
  else if (dados && dados.linhas.length === 0) aviso = 'A competência de origem não tem lançamentos de CIF.';

  return (
    <Dialog open onOpenChange={(proximo) => { if (!proximo) onFechar(); }}>
      <DialogContent className="w-[min(1180px,96vw)] max-w-none sm:max-w-none max-h-[90vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Copiar do mês…</DialogTitle>
          <DialogDescription>{`Copia os lançamentos de CIF de outra competência para ${rotuloCompetencia(destinoAtual)}. Edite o novo valor e, se quiser, a apropriação de cada linha.`}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-col gap-3">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="copia-cif-origem" className="text-caption font-medium text-ink-secondary">Competência de origem</label>
              <MonthPicker id="copia-cif-origem" value={origem} onChange={setOrigem} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="copia-cif-destino" className="text-caption font-medium text-ink-secondary">Competência de destino</label>
              <MonthPicker id="copia-cif-destino" value={destinoAtual} onChange={setDestinoAtual} />
            </div>
          </div>

          {destinoFechado && (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small font-medium text-danger-ink">
              <p>{`Rateio de ${rotuloCompetencia(destinoAtual)} fechado: não é possível copiar.`}</p>
              {podeEditar && <Button variant="outline" onClick={() => setReabrirAberto(true)}>Reabrir rateio</Button>}
            </div>
          )}
          {!destinoFechado && grade.isError && <p role="alert" className="text-small text-destructive">{grade.error instanceof ApiError ? grade.error.message : 'Não foi possível carregar a grade.'}</p>}
          {!destinoFechado && aviso && <p role="status" className="text-small font-medium text-ink-body">{aviso}</p>}
          {grade.isLoading && habilitado && <p role="status" className="text-small text-ink-muted">Carregando as CIFs da origem…</p>}

          {dados && linhas.length > 0 && (
            <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-border">
              <table className="w-full border-collapse text-small">
                <thead className="sticky top-0 z-10 bg-card text-left text-caption font-medium text-ink-secondary border-b border-border">
                  <tr>
                    <th className="px-3 py-2">
                      <input type="checkbox" aria-label="Marcar todas as linhas" checked={todasMarcadas} disabled={habilitadas.length === 0} onChange={(e) => marcarTodas(e.target.checked)} />
                    </th>
                    <th className="px-3 py-2">Conta/item</th>
                    <th className="px-3 py-2">Descrição</th>
                    <th className="px-3 py-2 text-right">Valor da origem</th>
                    <th className="px-3 py-2 text-right">Novo valor</th>
                    <th className="px-3 py-2">Apropriação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, indice) => {
                    const invalida = l.marcada && !l.existeNoDestino && valorDigitado(l) === null;
                    return (
                      <tr key={l.cifId} data-testid="linha-copia" className="border-t border-border align-top">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Copiar ${l.identificador}`}
                            checked={l.marcada && !l.existeNoDestino}
                            disabled={l.existeNoDestino}
                            onChange={(e) => alterar(l.cifId, { marcada: e.target.checked })}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-medium">{l.identificador}</span>{l.itemCifNome && l.itemCifNome !== l.identificador ? <span className="block text-caption text-ink-secondary">{l.itemCifNome}</span> : null}
                          {l.existeNoDestino && <span className="block text-caption text-ink-muted">já existe: será pulada</span>}
                        </td>
                        <td className="px-3 py-2 text-ink-secondary">{l.descricao}</td>
                        <td className="tabular px-3 py-2 text-right">{formatarMoeda(l.valorOrigem)}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            ref={(el) => {
                              if (el) campos.current.set(l.cifId, el);
                              else campos.current.delete(l.cifId);
                            }}
                            type="text"
                            inputMode="decimal"
                            aria-label={`Novo valor de ${l.identificador}`}
                            aria-invalid={invalida}
                            value={l.texto}
                            disabled={l.existeNoDestino}
                            onChange={(e) => alterar(l.cifId, { texto: e.target.value })}
                            onKeyDown={(e) => teclar(e, indice)}
                            onPaste={(e) => colar(e, indice)}
                            className={`${CAMPO} w-32 text-right tabular ${invalida ? 'border-destructive' : ''}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-ink-body">{resumoApropriacao(l.apropriacao, l.editada)}</span>
                            <Button variant="outline" size="sm" disabled={l.existeNoDestino} onClick={() => setEditandoId(l.cifId)}>Editar</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {erroCopia && <p role="alert" className="text-small text-destructive">{erroCopia}</p>}
        </div>

        <div className="flex flex-col gap-2">
          {totais && (
            <div className="flex flex-col gap-1 text-small text-ink-body">
              <p>
                {`Total da origem ${formatarMoeda(totais.origem)} × Total do destino ${formatarMoeda(totais.destino)} `}
                {`(já lançado ${formatarMoeda(dados?.totalDestinoAtual)} + a copiar ${formatarMoeda(totais.aCopiar)})`}
              </p>
              <p>{`${totais.marcadas} marcadas`}</p>
              {totais.invalidas > 0 && <p className="font-medium text-destructive">{`valor inválido em ${totais.invalidas} linha(s)`}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onFechar}>Cancelar</Button>
            <Button disabled={!podeCopiar} onClick={copiarAgora}>
              {copiar.isPending ? 'Copiando…' : 'Copiar'}
            </Button>
          </DialogFooter>
        </div>

        <Dialog open={editando !== undefined} onOpenChange={(proximo) => { if (!proximo) setEditandoId(null); }}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editando ? `Apropriação de ${editando.itemCifNome ?? editando.identificador}` : 'Apropriação'}</DialogTitle>
              <DialogDescription>Os percentuais são da API. Alterar aqui vale só para esta cópia.</DialogDescription>
            </DialogHeader>
            {editando && (
              <ApropriacaoPorCentro
                key={`${editando.cifId}-${versaoEditor}`}
                referTo="CIF"
                value={editando.editada ?? entradaDeResposta(editando.apropriacao)}
                valorBase={valorDigitado(editando) ?? undefined}
                onChange={(v) => {
                  if (v) alterar(editando.cifId, { editada: v });
                }}
              />
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  if (!editando) return;
                  alterar(editando.cifId, { editada: null });
                  setVersaoEditor((v) => v + 1);
                }}
              >
                Usar a da origem
              </Button>
              <Button onClick={() => setEditandoId(null)}>Concluir</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>

      <ReabrirRateioDialog
        competencia={destinoAtual}
        aberto={reabrirAberto}
        onFechar={() => setReabrirAberto(false)}
        onReaberto={() => {
          setFechadoPara(null);
          void grade.refetch();
        }}
      />
    </Dialog>
  );
}
